/**
 * Developer flag registry + resolver (O-026) — the PURE, node-testable core
 * behind the playtest control panel. Like quality.ts and capture.ts it imports
 * NO `three`, touches no DOM in its pure half, and does no I/O, so it runs under
 * `node --test` with no renderer and no jsdom.
 *
 * WHY THIS EXISTS. Every dev affordance in the techdemo used to be reached by
 * typing a URL flag, each read independently at its own call site from
 * `window.location.search`. Nothing knew what flags existed, so nothing could
 * render a UI from them. This module is that single source of truth.
 *
 * HOW IT STAYS LOW-RISK. It does not rewrite the existing parsers. It produces
 * an EFFECTIVE SEARCH STRING — the real URL search overlaid with the panel's
 * persisted overrides — which the existing pure parsers (`qualityForcedLow`,
 * `parseBloomParams`, `dungeonsEnabled`, `renderTier`, the collider check) then
 * consume unchanged. A hand-typed `?flag=1` URL therefore behaves exactly as it
 * did before, and remains authoritative over a stored override (so a shared link
 * and the studio's capture URLs stay reproducible).
 *
 * PRECEDENCE: URL param → persisted override → registry default.
 *
 * GATING: overrides apply only when the dev surface is enabled (`import.meta.env.DEV`
 * — a Vite compile-time literal, so the production bundle drops this path entirely).
 * When disabled the URL search is returned UNCHANGED: production behaviour is
 * byte-identical to before this module existed.
 *
 * Presentation-only and off the per-tick path: no sim, no wire, no collision, no
 * economy surface. Import boundary (AD-122): frontend-local, no shared/server imports.
 *
 * Design: docs/features/playtest-panel.md
 */

/** How soon a flag takes effect once changed from the panel. */
export type DevFlagApplies = 'live' | 'reload';

/** Which panel tab a flag belongs to. */
export type DevFlagGroup = 'dungeons' | 'render';

export interface DevFlagOption {
  readonly value: string;
  readonly label: string;
}

export interface DevFlagDef {
  /** Stable id — the storage key suffix and the panel's React key. */
  readonly id: string;
  /** The URL query parameter this flag is expressed as. */
  readonly param: string;
  readonly label: string;
  readonly help: string;
  readonly group: DevFlagGroup;
  readonly applies: DevFlagApplies;
  readonly kind: 'boolean' | 'enum' | 'text';
  /** `boolean` only: the param value written when the flag is ON. */
  readonly onValue?: string;
  /** `enum` only: the selectable values. `''` means unset (registry default applies). */
  readonly options?: readonly DevFlagOption[];
  /** `text` only: placeholder shown in the panel's input. */
  readonly placeholder?: string;
}

/**
 * Every dev flag the client understands, declared once.
 *
 * Adding a flag here makes it appear in the panel automatically. Keep `param`
 * matching the string its consumer parses — the resolver only writes params, it
 * does not interpret them, so a mismatch silently does nothing. `devFlags.test.ts`
 * pins the param names against that risk.
 */
export const DEV_FLAGS: readonly DevFlagDef[] = [
  {
    id: 'dungeons',
    param: 'dungeons',
    label: 'Dungeon shrines',
    help: 'Place threshold shrines in the joined zone. Needed before previews work.',
    group: 'dungeons',
    applies: 'reload',
    kind: 'boolean',
    onValue: '1',
  },
  {
    id: 'terrain',
    param: 'terrain',
    label: 'Flat ground',
    help: 'Draw flat ground instead of the bound terrain relief (AD-408). Relief is on by default since the follow camera frames a slope (q-follow-camera-vs-relief); this is the dev escape.',
    group: 'render',
    applies: 'reload',
    kind: 'boolean',
    onValue: '0',
  },
  {
    id: 'skystill',
    param: 'skystill',
    label: 'Still sky',
    help: 'Hold the cloud layer still (no drift) — reproducible frames for the lane (AD-9007).',
    group: 'render',
    applies: 'reload',
    kind: 'boolean',
    onValue: '1',
  },
  {
    id: 'colliders',
    param: 'colliders',
    label: 'Collider overlay',
    help: 'Draw collision footprints over the scene (WR.3.3).',
    group: 'render',
    applies: 'reload',
    kind: 'boolean',
    onValue: '1',
  },
  {
    id: 'quality',
    param: 'quality',
    label: 'Force low quality',
    help: 'Degraded profile: no SSAO, no bloom, half-size shadow maps. AgX retained.',
    group: 'render',
    applies: 'reload',
    kind: 'boolean',
    onValue: 'low',
  },
  {
    id: 'tier',
    param: 'tier',
    label: 'Render tier',
    help: 'Override the auto-detected budget tier (auto = coarse pointer ⇒ mobile).',
    group: 'render',
    applies: 'reload',
    kind: 'enum',
    options: [
      { value: '', label: 'Auto-detect' },
      { value: 'desktop', label: 'Desktop' },
      { value: 'mobile', label: 'Mobile' },
    ],
  },
  {
    id: 'renderer',
    param: 'renderer',
    label: 'Renderer',
    help: 'WebGPU is opt-in and falls back to WebGL2 when unsupported (AD-195).',
    group: 'render',
    applies: 'reload',
    kind: 'enum',
    options: [
      { value: '', label: 'WebGL2 (default)' },
      { value: 'webgpu', label: 'WebGPU' },
    ],
  },
  {
    id: 'bloom',
    param: 'bloom',
    label: 'Bloom override',
    help: 'threshold,strength,radius — partial values allowed (e.g. "0.9"). Clamped.',
    group: 'render',
    applies: 'reload',
    kind: 'text',
    placeholder: '0.85,0.35,0.4',
  },
  {
    id: 'budgetload',
    param: 'budgetload',
    label: 'Budget overload fixture',
    help: 'Add N extra draw calls (hidden one-triangle meshes) so the render-budget probe can be shown to fail (E3.3.S1).',
    group: 'render',
    applies: 'reload',
    kind: 'text',
    placeholder: '2000',
  },
];

/** localStorage namespace for persisted overrides (alongside the `pxl.*` prefs). */
export const DEV_FLAG_STORAGE_PREFIX = 'pxl.dev.';

/** A persisted override set: flag id → param value. An absent/empty entry means "unset". */
export type DevFlagOverrides = Readonly<Record<string, string>>;

export function devFlagById(id: string): DevFlagDef | undefined {
  return DEV_FLAGS.find((f) => f.id === id);
}

/**
 * Merge persisted overrides into a URL search string, producing the effective
 * search every dev-flag consumer should read.
 *
 * - `enabled === false` ⇒ `urlSearch` is returned UNCHANGED (production path).
 * - A param already present in `urlSearch` always wins over a stored override.
 * - Non-dev params in `urlSearch` are preserved untouched.
 * - An empty result is `''`, matching `window.location.search` when there is none.
 *
 * Pure: same inputs → same output.
 */
export function resolveEffectiveSearch(
  urlSearch: string,
  overrides: DevFlagOverrides,
  enabled: boolean,
): string {
  if (!enabled) return urlSearch;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(urlSearch);
  } catch {
    return urlSearch;
  }
  let changed = false;
  for (const def of DEV_FLAGS) {
    if (params.has(def.param)) continue; // the URL is authoritative
    const value = overrides[def.id];
    if (value === undefined || value === '') continue; // unset
    params.set(def.param, value);
    changed = true;
  }
  if (!changed) return urlSearch;
  const serialised = params.toString();
  return serialised === '' ? '' : `?${serialised}`;
}

/**
 * The value a flag currently resolves to, and where it came from. Drives the
 * panel's "set in URL" affordance — a URL-pinned flag is shown read-only,
 * because toggling it would silently do nothing.
 */
export interface DevFlagState {
  readonly value: string;
  readonly source: 'url' | 'override' | 'default';
}

export function resolveDevFlagState(
  def: DevFlagDef,
  urlSearch: string,
  overrides: DevFlagOverrides,
): DevFlagState {
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(urlSearch).get(def.param);
  } catch {
    fromUrl = null;
  }
  if (fromUrl !== null) return { value: fromUrl, source: 'url' };
  const stored = overrides[def.id];
  if (stored !== undefined && stored !== '') return { value: stored, source: 'override' };
  return { value: '', source: 'default' };
}

/** Whether a boolean flag reads as ON in the given state. */
export function devFlagIsOn(def: DevFlagDef, state: DevFlagState): boolean {
  if (def.kind !== 'boolean') return state.value !== '';
  return state.value === (def.onValue ?? '1');
}

// ---------------------------------------------------------------------------
// Browser side — thin, guarded, and never touched by the pure functions above
// (the capture.ts split: a non-DOM context is a safe no-op, not a throw).
// ---------------------------------------------------------------------------

function ls(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Is the developer surface available in this build?
 *
 * `import.meta.env.DEV` is replaced by Vite with a compile-time literal, so in a
 * production build this is `false` and every caller's branch is statically dead
 * — the panel, the registry UI and the jump hook do not ship. Read defensively
 * so the module also loads under the dependency-free test runner, where
 * `import.meta.env` is undefined (⇒ `false`; tests pass `enabled` explicitly).
 */
export function devToolsEnabled(): boolean {
  // The LITERAL `import.meta.env.DEV` is what Vite substitutes at build time. The old
  // `(import.meta as ...).env` cast hid it from that replacement, so the dev panels were still
  // bundled and only skipped at runtime (measured 2026-10-24, q-debug-panel-shipped-to-players).
  // Under `node --test` `import.meta.env` is undefined, hence the `?.`.
  return import.meta.env?.DEV === true;
}

/** Read the persisted override set. Absent keys are simply missing from the record. */
export function readDevOverrides(): DevFlagOverrides {
  const store = ls();
  if (store === null) return {};
  const out: Record<string, string> = {};
  for (const def of DEV_FLAGS) {
    const raw = store.getItem(`${DEV_FLAG_STORAGE_PREFIX}${def.id}`);
    if (raw !== null && raw !== '') out[def.id] = raw;
  }
  return out;
}

/** Persist one override. An empty value clears it (back to the registry default). */
export function writeDevOverride(id: string, value: string): void {
  const store = ls();
  if (store === null) return;
  const key = `${DEV_FLAG_STORAGE_PREFIX}${id}`;
  if (value === '') store.removeItem(key);
  else store.setItem(key, value);
}

/** Clear every persisted override (the panel's "Reset all" action). */
export function clearDevOverrides(): void {
  const store = ls();
  if (store === null) return;
  for (const def of DEV_FLAGS) store.removeItem(`${DEV_FLAG_STORAGE_PREFIX}${def.id}`);
}

/**
 * The effective search string for this session — what every dev-flag consumer
 * reads instead of `window.location.search`. Safe in a non-DOM context (`''`).
 */
export function effectiveSearch(): string {
  let search = '';
  try {
    search = typeof window === 'undefined' ? '' : window.location.search;
  } catch {
    return '';
  }
  return resolveEffectiveSearch(search, readDevOverrides(), devToolsEnabled());
}

/** The raw URL search, for the panel's "pinned in URL" display. Safe off-DOM. */
export function rawSearch(): string {
  try {
    return typeof window === 'undefined' ? '' : window.location.search;
  } catch {
    return '';
  }
}

/**
 * Whether the bound terrain relief is drawn for a page's query string
 * (`q-follow-camera-vs-relief`). ON by default: only `?terrain=0` (the
 * "Flat ground" dev flag) draws the flat ground. Before this the relief was
 * opt-in (`?terrain=1`) and every logged-in world rendered flat.
 */
export function terrainReliefOn(search: string): boolean {
  return new URLSearchParams(search).get('terrain') !== '0';
}
